import { SvelteMap } from 'svelte/reactivity'

import type { IModel } from '../models/models-store.svelte'
import { HirundoCSV } from '@utils/hirundo-csv.svelte'

enum UnlearningRunStatus {
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  STARTED = 'STARTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  REVOKED = 'REVOKED',
  IGNORED = 'IGNORED',
  REJECTED = 'REJECTED',
  AWAITING_MANUAL_APPROVAL = 'AWAITING_MANUAL_APPROVAL',
}

const displayRunStatus = {
  [UnlearningRunStatus.PENDING]: 'Pending',
  [UnlearningRunStatus.STARTED]: 'Started',
  [UnlearningRunStatus.SUCCESS]: 'Success',
  [UnlearningRunStatus.FAILURE]: 'Failure',
  [UnlearningRunStatus.RETRY]: 'Retry',
  [UnlearningRunStatus.REVOKED]: 'Cancelled',
  [UnlearningRunStatus.REJECTED]: 'Rejected',
  [UnlearningRunStatus.AWAITING_MANUAL_APPROVAL]: 'Awaiting approval',
}
const displayRunStatusSubtitle = {
  [UnlearningRunStatus.PENDING]: 'Run not yet started: Provisioning compute',
  [UnlearningRunStatus.STARTED]: 'Run in progress',
  [UnlearningRunStatus.SUCCESS]: '',
  [UnlearningRunStatus.FAILURE]: 'Run failed. Please check your dataset',
  [UnlearningRunStatus.RETRY]: 'Run retrying',
  [UnlearningRunStatus.REVOKED]: 'Run cancelled',
  [UnlearningRunStatus.REJECTED]: 'Run rejected. Try re-running',
  [UnlearningRunStatus.AWAITING_MANUAL_APPROVAL]: 'Run awaiting approval',
}

enum ScenarioType {
  RANDOM_SAMPLES = 'RANDOM_SAMPLES',
  CLASS_SUBSET = 'CLASS_SUBSET',
  FULL_CLASS = 'FULL_CLASS',
  UNSET = 'UNSET',
}

const scenarioTypesValues = [
  ScenarioType.UNSET,
  ScenarioType.RANDOM_SAMPLES,
  ScenarioType.CLASS_SUBSET,
  ScenarioType.FULL_CLASS,
] // not using Object.values because we want to set the order of the options

const displayScenarioTypes = {
  [ScenarioType.RANDOM_SAMPLES]: 'Random Samples',
  [ScenarioType.CLASS_SUBSET]: 'Class Subset',
  [ScenarioType.FULL_CLASS]: 'Full Class',
  [ScenarioType.UNSET]: 'Auto-detect',
}

type JSONOfThis<T> = {
  [K in keyof T]: T[K]
}

type NestedStrings = string | NestedStrings[]

abstract class UnlearningRunArgs {
  abstract getDisplayFields(): SvelteMap<keyof this & string, string>

  static deepSerializeObject(value: unknown): NestedStrings {
    if (value === null) {
      return []
    }
    if (typeof value === 'string') {
      return [value]
    }
    if (Array.isArray(value)) {
      return value.map((v, i) => [i.toString(), UnlearningRunArgs.deepSerializeObject(v)])
    }
    if (value instanceof Object) {
      return Object.entries(value).map(([key, v]) => [
        `${key} = ${UnlearningRunArgs.deepSerializeObject(v)}`,
      ])
    }
    return [JSON.stringify(value)]
  }

  displayRunArgs(): NestedStrings {
    return Array.from(this.getDisplayFields().entries()).map(([field, fieldName]) => {
      const value = this[field]
      if (value === null) {
        return []
      }
      if (value instanceof UnlearningRunArgs) {
        return [fieldName, ...value.displayRunArgs()]
      } else {
        return [`${fieldName} = ${UnlearningRunArgs.deepSerializeObject(value)}`]
      }
    })
  }

  toJSON() {
    return Array.from(this.getDisplayFields().keys()).reduce((acc, field) => {
      acc[field] = this[field]
      return acc
    }, {} as JSONOfThis<this>)
  }
}

class AugmentationParams extends UnlearningRunArgs {
  public name: string = $state('')
  public kwargs?: { [key: string]: number } = $state()

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['name', 'Name'],
      ['kwargs', 'Kwargs'],
    ])
  }

  static fromJSON(json: $state.Snapshot<AugmentationParams>) {
    const params = new AugmentationParams()
    params.name = json.name
    params.kwargs = json.kwargs
    return params
  }
}

class OptimizerParams extends UnlearningRunArgs {
  public name: string | null = $state(null)
  public kwargs?: { [key: string]: number | boolean } = $state()

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['name', 'Name'],
      ['kwargs', 'Kwargs'],
    ])
  }

  static fromJSON(json: $state.Snapshot<OptimizerParams>) {
    const params = new OptimizerParams()
    params.name = json.name
    params.kwargs = json.kwargs
    return params
  }
}

class LrFinderParams extends UnlearningRunArgs {
  active: boolean | null = $state(null)
  minLr: number | null = $state(null)
  maxLr: number | null = $state(null)
  numberOfTrainingSteps: number | null = $state(null)

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['active', 'Active'],
      ['minLr', 'Min LR'],
      ['maxLr', 'Max LR'],
      ['numberOfTrainingSteps', 'Number of Training Steps'],
    ])
  }

  static fromJSON(json: $state.Snapshot<LrFinderParams>) {
    const params = new LrFinderParams()
    params.active = json.active
    params.minLr = json.minLr
    params.maxLr = json.maxLr
    params.numberOfTrainingSteps = json.numberOfTrainingSteps
    return params
  }

  static get MIN_LR() {
    return 0.001
  }

  static get MAX_LR() {
    return 0.1
  }
}

class LrScheduler extends UnlearningRunArgs {
  name: string | null = $state(null)
  kwargs?: { [key: string]: number | number[] } = $state()

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['name', 'Name'],
      ['kwargs', 'Kwargs'],
    ])
  }

  static fromJSON(json: $state.Snapshot<LrScheduler>) {
    const params = new LrScheduler()
    params.name = json.name
    params.kwargs = json.kwargs
    return params
  }
}

enum ClassWeighting {
  UNIFORM = 'uniform',
  BALANCED = 'balanced',
  LOG_BALANCED = 'log_balanced',
}
const classWeightingValues = [
  ClassWeighting.UNIFORM,
  ClassWeighting.BALANCED,
  ClassWeighting.LOG_BALANCED,
] // not using Object.values because keys and values are not the same
const displayClassWeighting = {
  [ClassWeighting.UNIFORM]: 'Uniform',
  [ClassWeighting.BALANCED]: 'Balanced',
  [ClassWeighting.LOG_BALANCED]: 'Log Balanced',
}
class LossFunction extends UnlearningRunArgs {
  name: string | null = $state(null)
  kwargs?: { [key: string]: number | boolean | string } = $state()
  classWeighting: ClassWeighting | null = $state(null)

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['name', 'Name'],
      ['kwargs', 'Kwargs'],
      ['classWeighting', 'Class Weighting'],
    ])
  }

  static fromJSON(json: $state.Snapshot<LossFunction>) {
    const params = new LossFunction()
    params.name = json.name
    params.kwargs = json.kwargs
    params.classWeighting = json.classWeighting
    return params
  }
}

class TrainerParams extends UnlearningRunArgs {
  maxEpochs: number | null = $state(null)
  patience: number | null = $state(null)
  batchSize: number | null = $state(null)
  autoFindBatchSize: boolean | null = $state(true)
  optimizer: OptimizerParams | null = $state(null)
  lrFinder: LrFinderParams | null = $state(null)
  lrScheduler: LrScheduler | null = $state(null)
  lossFunction: LossFunction | null = $state(null)

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['maxEpochs', 'Max Epochs'],
      ['patience', 'Patience'],
      ['batchSize', 'Batch Size'],
      ['autoFindBatchSize', 'Auto Find Batch Size'],
      ['optimizer', 'Optimizer'],
      ['lrFinder', 'LR Finder'],
      ['lrScheduler', 'LR Scheduler'],
      ['lossFunction', 'Loss Function'],
    ])
  }

  static fromJSON(json: $state.Snapshot<TrainerParams>) {
    const params = new TrainerParams()
    params.maxEpochs = json.maxEpochs
    params.patience = json.patience
    params.batchSize = json.batchSize
    params.autoFindBatchSize = json.autoFindBatchSize
    params.optimizer = json.optimizer
      ? OptimizerParams.fromJSON(json.optimizer as $state.Snapshot<OptimizerParams>)
      : null
    params.lrFinder = json.lrFinder ? LrFinderParams.fromJSON(json.lrFinder) : null
    params.lrScheduler = json.lrScheduler
      ? LrScheduler.fromJSON(json.lrScheduler as $state.Snapshot<LrScheduler>)
      : null
    params.lossFunction = json.lossFunction ? LossFunction.fromJSON(json.lossFunction) : null
    return params
  }

  prepare(field: TrainerFields) {
    if (field === 'optimizer' && !this.optimizer) {
      this.optimizer = new OptimizerParams()
      return true
    } else if (field === 'lrFinder' && !this.lrFinder) {
      this.lrFinder = new LrFinderParams()
      return true
    } else if (field === 'lrScheduler' && !this.lrScheduler) {
      this.lrScheduler = new LrScheduler()
      return true
    } else if (field === 'lossFunction' && !this.lossFunction) {
      this.lossFunction = new LossFunction()
      return true
    }
    return false
  }
}

const DEFAULT_IMAGE_SIZE = 224
const DEFAULT_LEARNING_RATE_FINDER_MIN_LR_STEP = 0.001
const DEFAULT_LEARNING_RATE_FINDER_MAX_LR_STEP = 0.01
const DEFAULT_LEARNING_RATE_FINDER_NUMBER_OF_TRAINING_STEPS = 100
const DEFAULT_TRAINER_MAX_EPOCHS = 10
const DEFAULT_TRAINER_PATIENCE = 5
const DEFAULT_TRAINER_BATCH_SIZE = 128

class UnlearningHyperparameters extends UnlearningRunArgs {
  imageSize: [number, number] | null = $state(null)
  augmentations: AugmentationParams[] | null = $state(null)
  trainer: TrainerParams | null = $state(null)

  getDisplayFields(): SvelteMap<keyof this & string, string> {
    return new SvelteMap([
      ['imageSize', 'Image Size'],
      ['augmentations', 'Augmentations'],
      ['trainer', 'Trainer'],
    ])
  }

  constructor(
    imageSize: [number, number] | null = null,
    augmentations: AugmentationParams[] | null = null,
    trainer: TrainerParams | null = null,
  ) {
    super()
    this.imageSize = imageSize
    this.augmentations = augmentations
    this.trainer = trainer
  }

  static fromJSON(json: $state.Snapshot<UnlearningHyperparameters>) {
    return new UnlearningHyperparameters(
      json.imageSize,
      json.augmentations?.map((augmentation) =>
        AugmentationParams.fromJSON(augmentation as $state.Snapshot<AugmentationParams>),
      ),
      json.trainer ? TrainerParams.fromJSON(json.trainer) : null,
    )
  }

  defineImageSize() {
    if (this.imageSize) {
      return this.imageSize
    }
    this.imageSize = [DEFAULT_IMAGE_SIZE, DEFAULT_IMAGE_SIZE]
    return this.imageSize
  }

  prepare(field: HyperParamFields) {
    if (field === 'imageSize' && !this.imageSize) {
      return this.defineImageSize()
    } else if (field === 'augmentations' && !this.augmentations) {
      this.augmentations = []
      return true
    } else if (field === 'trainer' && !this.trainer) {
      this.trainer = new TrainerParams()
      return true
    }

    return false
  }
}

const displayHyperparameters = (
  hyperparameters: ReturnType<typeof UnlearningHyperparameters.prototype.toJSON> | null,
) => {
  if (!hyperparameters) {
    return []
  }
  const hyperparametersInstance = UnlearningHyperparameters.fromJSON(hyperparameters)
  const displayRunArgs = hyperparametersInstance.displayRunArgs()
  if (
    displayRunArgs.length === 0 ||
    (Array.isArray(displayRunArgs) && displayRunArgs.every((arg) => arg.length === 0))
    // ^ Check if all arguments have not been set
  ) {
    return ['No advanced run options']
  }
  return displayRunArgs
}

type HyperParamFields = keyof UnlearningHyperparameters

type HyperParametersWithRequiredField<field extends HyperParamFields> = UnlearningHyperparameters &
  Record<field, NonNullable<UnlearningHyperparameters[field]>>

type TrainerFields = keyof TrainerParams

type TrainerWithRequiredField<field extends TrainerFields> = TrainerParams &
  Record<field, NonNullable<TrainerParams[field]>>

type UnlearningTaskInfo = HirundoCSV

enum DatasetSplit {
  FORGET = 'FORGET',
  RETAIN = 'RETAIN',
  TEST = 'TEST',
}

const displayDatasetSplit = {
  [DatasetSplit.FORGET]: 'Forget',
  [DatasetSplit.RETAIN]: 'Retain',
  [DatasetSplit.TEST]: 'Test',
}

interface IUnlearningRun {
  id: number
  name: string
  modelId: number
  model: IModel
  scenario: ScenarioType
  hyperparameters: ReturnType<typeof UnlearningHyperparameters.prototype.toJSON> | null
  unlearningTaskInfo: UnlearningTaskInfo
  predictAtEnd: DatasetSplit[] | null
  runId: string
  mlflowRunId: string
  status: UnlearningRunStatus
  approved: boolean
  preProcessProgress: number
  optimizationProgress: number
  postProcessProgress: number
  clearmlTaskId: string
  createdAt: Date
  deletedAt: Date | null
}
type IRawUnlearningRun = Omit<IUnlearningRun, 'createdAt' | 'deletedAt'> & {
  createdAt: string
  deletedAt: string | null
}

class RunInfo {
  public organizationId: number = $state(-1)

  public name = $state('')

  public scenario: ScenarioType = $state(ScenarioType.UNSET)
  public hyperparameters: UnlearningHyperparameters | null = $state(null)
  public unlearningTaskInfo: UnlearningTaskInfo | null = $state(null)
  public predictAtEnd: DatasetSplit[] = $state([DatasetSplit.TEST])

  constructor(organizationId: number) {
    this.organizationId = organizationId
  }

  toJSON() {
    return {
      organizationId: this.organizationId,
      name: this.name,
      scenario: this.scenario,
      hyperparameters: this.hyperparameters,
      unlearningTaskInfo: this.unlearningTaskInfo,
      predictAtEnd: this.predictAtEnd,
    }
  }
}

export type {
  HyperParametersWithRequiredField,
  HyperParamFields,
  IRawUnlearningRun,
  IUnlearningRun,
  NestedStrings,
  TrainerFields,
  TrainerWithRequiredField,
}
export {
  AugmentationParams,
  ClassWeighting,
  classWeightingValues,
  DatasetSplit,
  DEFAULT_IMAGE_SIZE,
  DEFAULT_LEARNING_RATE_FINDER_MAX_LR_STEP,
  DEFAULT_LEARNING_RATE_FINDER_MIN_LR_STEP,
  DEFAULT_LEARNING_RATE_FINDER_NUMBER_OF_TRAINING_STEPS,
  DEFAULT_TRAINER_BATCH_SIZE,
  DEFAULT_TRAINER_MAX_EPOCHS,
  DEFAULT_TRAINER_PATIENCE,
  displayClassWeighting,
  displayDatasetSplit,
  displayHyperparameters,
  displayRunStatus,
  displayRunStatusSubtitle,
  displayScenarioTypes,
  LrFinderParams,
  RunInfo,
  ScenarioType,
  scenarioTypesValues,
  UnlearningHyperparameters,
  UnlearningRunStatus,
}
